import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Sparkles, Star, Target, TrendingUp } from 'lucide-react';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import { useStudentProgressAnalyticsData } from '../../modules/student-dashboard/hooks/useStudentProgressAnalyticsData';
import { ROUTES } from '../../utils/constants';
import {
  friendlyRecommendation,
  lessonScorePercent,
  lessonScoreStars,
  partitionSkills,
  rubricScoreNum,
  skillDisplayTitle,
  skillLevelFromRubric,
  SKILL_LEVEL_UI,
  type CanDoLookup,
} from './studentLessonProgressFriendly';
import { useTutorTenantLabels } from '../../modules/tutor/hooks/useTutorTenantLabels';

const ACCENT_GRADIENT = 'bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75';
const BUTTON_GRADIENT =
  'bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75 hover:brightness-110 shadow-lg shadow-app-primary/25';
const PANEL_SHELL =
  'rounded-[1.75rem] border border-app-primary/20 bg-white p-5 shadow-xl shadow-app-primary/15 ring-1 ring-slate-200/80 dark:border-app-primary/30 dark:bg-gray-800 dark:shadow-[0_24px_64px_-24px_rgba(0,0,0,0.35)] dark:ring-white/5 sm:p-7';
const ICON_BADGE =
  'rounded-2xl bg-gradient-to-br from-app-primary/10 via-app-primary/5 to-app-primary/5 p-3 ring-1 ring-app-primary/20 dark:from-app-primary/25 dark:via-app-primary/15 dark:to-app-primary/10 dark:ring-app-primary/35';
const HEADER_DIVIDER = 'border-b border-slate-200 dark:border-white/10 pb-5';
const EMPTY_BOX =
  'rounded-2xl border border-dashed border-app-primary/30 bg-app-primary/5 px-4 py-8 text-center text-sm text-slate-600 min-h-[5.5rem] flex items-center justify-center dark:border-app-primary/35 dark:bg-white/[0.04] dark:text-white/55';
const TIPS_BOX =
  'rounded-2xl border border-dashed border-app-primary/30 bg-app-primary/5 p-4 text-sm text-slate-700 dark:border-app-primary/35 dark:bg-white/[0.04] dark:text-white/80';

function SkillCard({
  row,
  lookup,
  variant,
}: {
  row: Record<string, unknown>;
  lookup: CanDoLookup;
  variant: 'strength' | 'focus';
}) {
  const { t } = useTranslation();
  const avg = rubricScoreNum(row);
  const level = skillLevelFromRubric(avg);
  const ui = SKILL_LEVEL_UI[level];
  const levelLabel = t(`dashboard.studentHome.skillLevel.${level}`, { defaultValue: ui.label });
  const { title, subtitle } = skillDisplayTitle(row, lookup);
  const border =
    variant === 'strength'
      ? 'border-emerald-200 dark:border-emerald-800/60'
      : 'border-amber-200 dark:border-amber-800/60';

  return (
    <li
      className={`rounded-2xl border-2 ${border} bg-white p-4 shadow-sm shadow-purple-50/50 dark:bg-white/[0.07] dark:shadow-black/25`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${ui.chipClass}`}
          >
            <span aria-hidden>{ui.emoji}</span>
            {levelLabel}
          </span>
          <p className="mt-2 text-sm font-bold leading-snug text-slate-900 dark:text-white line-clamp-3">
            {title}
          </p>
          {subtitle ? (
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 line-clamp-2">{subtitle}</p>
          ) : null}
        </div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-app-primary/15 dark:bg-white/10">
        <div
          className={`h-full rounded-full ${ACCENT_GRADIENT} transition-all duration-500`}
          style={{ width: `${ui.barPct}%` }}
        />
      </div>
    </li>
  );
}

function scoreFriendlyTranslated(
  pct: number | null,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (pct == null) return t('dashboard.studentHome.scoreFriendly.notScored');
  if (pct >= 75) return t('dashboard.studentHome.scoreFriendly.amazing');
  if (pct >= 50) return t('dashboard.studentHome.scoreFriendly.goodJob');
  if (pct >= 25) return t('dashboard.studentHome.scoreFriendly.keepGoing');
  if (pct > 0) return t('dashboard.studentHome.scoreFriendly.niceTry');
  return t('dashboard.studentHome.scoreFriendly.tryAgain');
}

function LessonProgressCard({
  lessonNo,
  scorePct,
  sessions,
}: {
  lessonNo: number;
  scorePct: number | null;
  sessions: number | null;
}) {
  const { t } = useTranslation();
  const stars = lessonScoreStars(scorePct);
  const label = scoreFriendlyTranslated(scorePct, t);

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50/90 to-white p-4 shadow-sm shadow-purple-50/40 dark:border-purple-500/35 dark:from-purple-600/15 dark:to-gray-900/40 dark:shadow-black/20">
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-lg font-black text-white shadow-md ${ACCENT_GRADIENT}`}
      >
        {lessonNo}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-slate-900 dark:text-white">
          {t('dashboard.studentHome.lessonN', { n: lessonNo })}
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-400">{label}</p>
        <div
          className="mt-1 flex items-center gap-0.5"
          aria-label={t('dashboard.studentHome.starsAria', { stars })}
        >
          {[1, 2, 3, 4].map((i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i <= stars
                  ? 'fill-amber-400 text-amber-400'
                  : 'fill-slate-200 text-slate-200 dark:fill-slate-600 dark:text-slate-600'
              }`}
              aria-hidden
            />
          ))}
        </div>
      </div>
      {sessions != null && sessions > 0 ? (
        <span className="shrink-0 text-[11px] font-semibold text-app-primary dark:text-purple-300">
          {sessions}{' '}
          {sessions === 1 ? t('dashboard.studentHome.time') : t('dashboard.studentHome.times')}
        </span>
      ) : null}
    </div>
  );
}

export const StudentProgressAnalyticsPanel: React.FC<{
  compact?: boolean;
}> = ({ compact }) => {
  const { t } = useTranslation();
  const { isSchoolFeatureEnabled } = useFeatureAccess();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const { data, canDoLookup, loading, error, emptyMessage, reload } =
    useStudentProgressAnalyticsData();

  const allSkills = useMemo(() => {
    const seen = new Set<string>();
    const rows: Array<Record<string, unknown>> = [];
    for (const row of [...(data?.strengths || []), ...(data?.weaknesses || [])]) {
      const id = String(row._id ?? row.can_do_id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      rows.push(row);
    }
    return rows;
  }, [data?.strengths, data?.weaknesses]);

  const { strengths, focus } = useMemo(
    () => partitionSkills(allSkills.length ? allSkills : []),
    [allSkills],
  );

  const friendlyTips = useMemo(() => {
    return (data?.recommendations || []).map((r) =>
      friendlyRecommendation(r, canDoLookup, {
        practiceMorePrefix: t('dashboard.studentHome.practiceMorePrefix'),
        revisitLesson: (n) =>
          t('dashboard.studentHome.revisitLesson', { n, tutor: labels.moduleTitleAiTutor }),
        extraPractice: t('dashboard.studentHome.extraPractice'),
      })
    );
  }, [data?.recommendations, canDoLookup, t, labels.moduleTitleAiTutor]);

  const lessons = useMemo(
    () =>
      [...(data?.lessons || [])].sort(
        (a, b) => (Number(b.lesson_no) || 0) - (Number(a.lesson_no) || 0),
      ),
    [data?.lessons],
  );

  const trend = useMemo(
    () =>
      (data?.score_trend || [])
        .filter((p) => p?.at)
        .slice(-8)
        .map((p) => ({
          at: p.at ? new Date(String(p.at)) : null,
          pct: lessonScorePercent(p.overall_score),
        })),
    [data?.score_trend],
  );

  if (!isSchoolFeatureEnabled('ai_tutor')) return null;
  // Malay formative progress lives in MalayLearningProgressSection — do not
  // invent can-do strengths / "no focus areas" praise for zero evidence.
  if (labelsLoading || labels.isMalay) return null;

  if (loading) {
    return (
      <section className={`${compact ? 'mt-6' : 'mt-8'} ${PANEL_SHELL}`}>
        <div className="flex items-center gap-3 text-slate-600 dark:text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin shrink-0 text-app-primary" aria-hidden />
          <span className="text-sm font-medium">{labels.skillsLoadingLabel}</span>
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className={`${compact ? 'mt-6' : 'mt-8'} ${PANEL_SHELL}`}>
        <header className={`flex flex-col gap-4 ${HEADER_DIVIDER} sm:flex-row sm:items-center sm:justify-between`}>
          <div className="flex items-center gap-3">
            <div className={ICON_BADGE}>
              <Sparkles className="h-7 w-7 text-app-primary dark:text-app-primary/80" aria-hidden />
            </div>
            <h2 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              {labels.progressTitle}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => void reload()}
            className={`inline-flex shrink-0 items-center justify-center rounded-full px-5 py-2.5 text-sm font-bold text-white transition hover:scale-[1.03] active:scale-[0.98] ${BUTTON_GRADIENT}`}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            {t('dashboard.studentHome.retry')}
          </button>
        </header>
        <div className="mt-5 space-y-2">
          <p className="text-sm text-slate-600 dark:text-slate-400">{error ?? emptyMessage}</p>
          <Link
            to={ROUTES.STUDENT_TAMIL_TUTOR}
            className="inline-flex text-sm font-bold text-app-primary underline dark:text-purple-300"
          >
            {labels.openTutorLinkLabel}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className={`${compact ? 'mt-6' : 'mt-8'} ${PANEL_SHELL}`}>
      <header className={`flex flex-col gap-4 ${HEADER_DIVIDER} sm:flex-row sm:items-center sm:justify-between`}>
        <div className="flex items-center gap-3">
          <div className={ICON_BADGE}>
            <Sparkles className="h-7 w-7 text-app-primary dark:text-app-primary/80" aria-hidden />
          </div>
          <div>
            <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl dark:text-white">
              {labels.progressTitle}
            </h2>
            <p className="text-xs font-medium text-app-primary/85 dark:text-app-primary/75">
              {t('dashboard.studentHome.gradeSeeWhat', { grade: data.grade })}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void reload()}
          className={`inline-flex shrink-0 items-center justify-center rounded-full px-5 py-2.5 text-sm font-bold text-white transition hover:scale-[1.03] active:scale-[0.98] ${BUTTON_GRADIENT}`}
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          {t('dashboard.studentHome.refresh')}
        </button>
      </header>

      <div className="mt-6 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <section>
            <h3 className="flex items-center gap-2 text-sm font-black text-emerald-700 dark:text-emerald-400 mb-3">
              <Star className="w-4 h-4 fill-emerald-500 text-emerald-500" aria-hidden />
              {t('dashboard.studentHome.doingGreatAt')}
            </h3>
            <ul className="space-y-3">
              {strengths.slice(0, compact ? 3 : 5).map((row, i) => (
                <SkillCard key={i} row={row} lookup={canDoLookup} variant="strength" />
              ))}
              {!strengths.length && (
                <li className={EMPTY_BOX}>
                  {labels.keepPractisingHint}
                </li>
              )}
            </ul>
          </section>
          <section>
            <h3 className="flex items-center gap-2 text-sm font-black text-amber-700 dark:text-amber-400 mb-3">
              <Target className="w-4 h-4" aria-hidden />
              {t('dashboard.studentHome.practiceALittleMore')}
            </h3>
            <ul className="space-y-3">
              {focus.slice(0, compact ? 3 : 5).map((row, i) => (
                <SkillCard key={i} row={row} lookup={canDoLookup} variant="focus" />
              ))}
              {!focus.length && (
                <li className={EMPTY_BOX}>
                  {labels.positiveGuidance}
                </li>
              )}
            </ul>
          </section>
        </div>

        {friendlyTips.length > 0 && (
          <section className={TIPS_BOX}>
            <h3 className="text-sm font-black text-app-primary dark:text-app-primary/90 mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" aria-hidden />
              {t('dashboard.studentHome.tipsForYou')}
            </h3>
            <ul className="space-y-2 text-sm text-slate-700 dark:text-white/85">
              {friendlyTips.map((tip, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-app-primary dark:text-app-primary/80 font-bold" aria-hidden>
                    •
                  </span>
                  <span className="text-slate-700 dark:text-white/85">{tip}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {lessons.length > 0 && (
          <section>
            <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 mb-3">
              {t('dashboard.studentHome.lessonsYouTried')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {lessons.slice(0, compact ? 3 : 6).map((row, i) => (
                <LessonProgressCard
                  key={i}
                  lessonNo={Number(row.lesson_no) || 0}
                  scorePct={lessonScorePercent(row.overall_score)}
                  sessions={row.sessions_count != null ? Number(row.sessions_count) : null}
                />
              ))}
            </div>
          </section>
        )}

        {trend.length > 0 && (
          <section>
            <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 mb-3">
              {t('dashboard.studentHome.scoresGrowing')}
            </h3>
            <div className="flex flex-wrap items-end gap-2">
              {trend.map((p, i) => {
                const pct = p.pct ?? 0;
                const h = Math.max(12, Math.round((pct / 100) * 72));
                return (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-1 min-w-[3.5rem]"
                    title={p.at ? p.at.toLocaleDateString() : undefined}
                  >
                    <div
                      className={`w-8 rounded-t-lg ${ACCENT_GRADIENT} opacity-90`}
                      style={{ height: `${h}px` }}
                    />
                    <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 tabular-nums">
                      {pct > 0 ? `${pct}%` : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="text-center pt-2">
          <Link
            to={ROUTES.STUDENT_TAMIL_TUTOR}
            className={`inline-flex min-h-[44px] items-center justify-center rounded-full px-8 text-sm font-bold text-white transition hover:scale-[1.03] active:scale-[0.98] ${BUTTON_GRADIENT}`}
          >
            {labels.practiceCta}
          </Link>
        </div>
      </div>
    </section>
  );
};
